import { useEffect, useMemo } from "react";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as retailerLocationGroupFields from "utils/schemas/retailerLocationGroup";
import Form, { FormProps } from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import {
  makePostgraphileErrorProcessor,
  getPostgraphileErrorMatchersForFields,
} from "utils/PostgraphileErrorExtensions";
import { RetailerLocationGroupInput } from "__generated__/gql/graphql";
import CreatableRetailerSelect from "components/Shared/Select/CreatableRetailerSelect";

export const FORM_FIELDS = ["retailerId", "name"] as const;

export type FormFields = Pick<
  RetailerLocationGroupInput,
  typeof FORM_FIELDS[number]
>;

const schema: yup.ObjectSchema<FormFields> = yup.object().shape({
  retailerId: retailerLocationGroupFields.retailerId,
  name: retailerLocationGroupFields.name,
});

const { FormField, ControlledFormField } =
  createFormFieldComponents<FormFields>({
    schema,
  });

export const processApolloError = makePostgraphileErrorProcessor<FormFields>(
  getPostgraphileErrorMatchersForFields<keyof FormFields>(FORM_FIELDS, {
    type: "RetailerLocationGroup",
  }),
);

type Props = Omit<FormProps<FormFields, object>, "formContext"> & {
  // eslint-disable-next-line react/boolean-prop-naming
  autoFocus?: boolean;
  defaultValues?: Partial<FormFields>;
  isAdmin?: boolean;
  retailerId?: UUID;
};

export default function RetailerLocationGroupForm({
  autoFocus,
  defaultValues: defaultValuesProp,
  children,
  isAdmin,
  retailerId,
  ...rest
}: Props) {
  const defaultValues = useMemo(() => {
    if (!retailerId) return defaultValuesProp;
    return { ...defaultValuesProp, retailerId };
  }, [defaultValuesProp, retailerId]);

  const formContext = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      formContext.reset(defaultValues);
    }
  }, [defaultValues, formContext]);

  return (
    <Form {...{ formContext }} {...rest}>
      <ControlledFormField
        name="retailerId"
        isDisabled={!!retailerId}
        renderInput={({ field }) => (
          <CreatableRetailerSelect
            {...field}
            isAdmin={isAdmin}
            autoFocus={defaultValues?.retailerId ? undefined : autoFocus}
            isDisabled={!!retailerId}
          />
        )}
      />

      <FormField
        name="name"
        mt={0}
        autoFocus={defaultValues?.retailerId ? autoFocus : undefined}
      />

      {children}
    </Form>
  );
}
